import { useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../api/invoices';
import { personsApi } from '../../api/persons';
import { taxRatesApi } from '../../api/taxRates';
import { accountsApi } from '../../api/accounts';
import { cround, formatMoney } from '../../utils/formatMoney';
import LoadingSpinner from '../../components/LoadingSpinner';
import type { UpdateInvoiceMadeRequest, UpdateInvoiceReceivedRequest, UpdateInvoiceLineRequest } from '../../types/invoice';
import { useAppBack, useBeforeUnload } from '../../hooks/useAppBack';

interface LineForm {
  name: string;
  unit: string;
  account: string;
  vatAccount: string;
  unitPrice: string;
  quantity: string;
  vatRateId: string;
}

const emptyLine = (): LineForm => ({
  name: '',
  unit: 'vnt.',
  account: '',
  vatAccount: '',
  unitPrice: '0',
  quantity: '1',
  vatRateId: '',
});

export default function InvoiceEdit() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const direction = searchParams.get('direction') ?? 'Made';

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice-detail', id, direction],
    queryFn: () => invoicesApi.getById(Number(id), direction),
    enabled: !!id,
  });

  const [date, setDate] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [content, setContent] = useState('');
  const [personId, setPersonId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [currencyRate, setCurrencyRate] = useState('1');
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  // Finding 35: unsaved-invoice guard — USER edits only (the form's onChange
  // delegation fires on real input events, never on the programmatic
  // populate useMemo above).
  const [dirty, setDirty] = useState(false);
  useAppBack({ enabled: true, blocking: dirty });
  useBeforeUnload(dirty);

  // Populate form when invoice data loads
  useMemo(() => {
    if (invoice && !initialized) {
      setDate(invoice.date?.split('T')[0] ?? '');
      setDocumentNumber(invoice.documentNumber);
      setContent(invoice.content);
      setPersonId(invoice.personId?.toString() ?? '');
      setCurrencyCode(invoice.currencyCode);
      setCurrencyRate(invoice.currencyRate.toString());
      if (invoice.lines.length > 0) {
        setLines(
          invoice.lines.map((l) => ({
            name: l.name,
            unit: l.unit || 'vnt.',
            account: l.account.toString(),
            vatAccount: l.vatAccount?.toString() ?? '',
            unitPrice: l.unitPrice.toString(),
            quantity: l.quantity.toString(),
            vatRateId: '',
          }))
        );
      }
      setInitialized(true);
    }
  }, [invoice, initialized]);

  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates-all'],
    queryFn: taxRatesApi.list,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; payload: UpdateInvoiceMadeRequest | UpdateInvoiceReceivedRequest }) =>
      direction === 'Made'
        ? invoicesApi.updateMade(data.id, data.payload as UpdateInvoiceMadeRequest)
        : invoicesApi.updateReceived(data.id, data.payload as UpdateInvoiceReceivedRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', id, direction] });
      navigate(`/invoices/${id}?direction=${direction}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateLine = (index: number, field: keyof LineForm, value: string) => {
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current) {
        next[index] = { ...current, [field]: value } as LineForm;
      }
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const getTaxRate = (id: string) => {
    if (!id) return 0;
    return taxRates?.find((r) => r.id === Number(id))?.rate ?? 0;
  };

  const buildLineRequest = (line: LineForm): { form: LineForm; request: UpdateInvoiceLineRequest } => {
    const unitPrice = parseFloat(line.unitPrice) || 0;
    const quantity = parseFloat(line.quantity) || 0;
    const vatRate = getTaxRate(line.vatRateId);
    const sum = cround(unitPrice * quantity);
    const sumVat = cround(sum * vatRate);

    return {
      form: line,
      request: {
        name: line.name,
        unit: line.unit || 'vnt.',
        account: parseInt(line.account) || 0,
        vatAccount: line.vatAccount ? parseInt(line.vatAccount) : null,
        unitPrice,
        quantity,
        vatRate,
        sum,
        sumVat,
      },
    };
  };

  const computedLines = lines.map(buildLineRequest);
  const totalSum = computedLines.reduce((acc, l) => acc + l.request.sum, 0);
  const totalVat = computedLines.reduce((acc, l) => acc + l.request.sumVat, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!documentNumber.trim()) { setError('Dokumento numeris yra būtinas'); return; }
    if (!content.trim()) { setError('Turinys yra būtinas'); return; }
    if (!personId) { setError(`${direction === 'Made' ? 'Klientas' : 'Tiekėjas'} yra būtinas`); return; }

    const lineRequests = computedLines.map((l) => l.request);
    const invalidLine = lineRequests.find((l) => !l.account || !l.name);
    if (invalidLine) { setError('Kiekviena eilutė turi turėti pavadinimą ir sąskaitą'); return; }

    const payload = {
      date,
      documentNumber: documentNumber.trim(),
      content: content.trim(),
      personId: parseInt(personId),
      currencyCode,
      currencyRate: parseFloat(currencyRate) || 1,
      lines: lineRequests,
    };

    updateMutation.mutate({ id: Number(id), payload });
  };

  if (isLoading) return <LoadingSpinner />;
  if (!invoice) return <div className="text-center py-8 text-gray-500">Sąskaita faktūra nerasta</div>;

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          Redaguoti sąskaitą faktūrą Nr. {invoice.documentNumber}
        </h1>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          onClick={() => navigate(-1)}
        >
          Atšaukti
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}

      <form onChange={() => setDirty(true)} onSubmit={handleSubmit} className="space-y-4">
        {/* Header fields */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Kryptis</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50"
                value={direction}
                disabled
              >
                <option value="Made">Pardavimas (išrašyta)</option>
                <option value="Received">Pirkimas (gauta)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data</label>
              <input
                type="date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dokumento nr.</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{direction === 'Made' ? 'Klientas' : 'Tiekėjas'}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
              >
                <option value="">-- Pasirinkite --</option>
                {persons?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiuta</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiutos kursas</label>
              <input
                type="number"
                step="0.0001"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyRate}
                onChange={(e) => setCurrencyRate(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Turinys</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-sm">Eilutės</h2>
            <button
              type="button"
              className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
              onClick={addLine}
            >
              + Pridėti eilutę
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left min-w-32">Pavadinimas</th>
                  <th className="text-left w-20">Vnt.</th>
                  <th className="text-right w-20">Sąskaita</th>
                  <th className="text-right w-20">Kaina</th>
                  <th className="text-right w-16">Kiekis</th>
                  <th className="text-left w-28">PVM tarifas</th>
                  <th className="text-right w-20">Suma</th>
                  <th className="text-right w-20">PVM</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const built = buildLineRequest(line);
                  return (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.name}
                          onChange={(e) => updateLine(i, 'name', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.unit}
                          onChange={(e) => updateLine(i, 'unit', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.account}
                          onChange={(e) => updateLine(i, 'account', e.target.value)}
                          required
                        >
                          <option value="">--</option>
                          {accounts?.map((a) => (
                            <option key={a.accountNumber} value={a.accountNumber}>
                              {a.accountNumber} {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.unitPrice}
                          onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.vatRateId}
                          onChange={(e) => updateLine(i, 'vatRateId', e.target.value)}
                        >
                          <option value="">Be PVM</option>
                          {taxRates?.filter((r) => !r.isObsolete).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} ({(r.rate * 100).toFixed(0)}%)
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num">{formatMoney(built.request.sum, false)}</td>
                      <td className="num">{formatMoney(built.request.sumVat, false)}</td>
                      <td>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeLine(i)}
                          disabled={lines.length <= 1}
                        >
                          x
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-right font-semibold">Viso</td>
                  <td className="num font-semibold">{formatMoney(totalSum)}</td>
                  <td className="num font-semibold">{formatMoney(totalVat)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={6} className="text-right font-bold">Viso su PVM</td>
                  <td colSpan={2} className="num font-bold text-primary-700">{formatMoney(totalSum + totalVat)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={() => navigate(-1)}
          >
            Atšaukti
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Išsaugoma…' : 'Išsaugoti pakeitimus'}
          </button>
        </div>
      </form>
    </div>
  );
}
